import React, { useState, useRef } from 'react';
import { UserCircle, Building2, ArrowRight, Upload, X, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Layout from '../components/layout/Layout';
import { careerSectors } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareersPage = () => {
  const [activeTab, setActiveTab] = useState('candidate');
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    age: '',
    qualification: '',
    location: '',
    mobile: '',
    email: '',
    sector: '',
    resume: null
  });
  const [employerForm, setEmployerForm] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    location: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleCandidateChange = (e) => {
    const { name, value } = e.target;
    setCandidateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEmployerChange = (e) => {
    const { name, value } = e.target;
    setEmployerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      setCandidateForm(prev => ({ ...prev, resume: file }));
    }
  };

  const removeFile = () => {
    setCandidateForm(prev => ({ ...prev, resume: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCandidateSubmit = async (e) => {
    e.preventDefault();
    
    if (!candidateForm.qualification || !candidateForm.sector) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: candidateForm.name,
        age: parseInt(candidateForm.age),
        qualification: candidateForm.qualification,
        location: candidateForm.location,
        mobile: candidateForm.mobile,
        email: candidateForm.email || null,
        sector: candidateForm.sector,
        resume_url: null // File upload can be added later
      };
      
      await axios.post(`${API}/candidates`, payload);
      
      toast.success('Registration submitted successfully!', {
        description: 'Our team will contact you shortly with suitable opportunities.'
      });
      
      setCandidateForm({
        name: '',
        age: '',
        qualification: '',
        location: '',
        mobile: '',
        email: '',
        sector: '',
        resume: null
      });
    } catch (error) {
      console.error('Error submitting candidate form:', error);
      toast.error('Failed to submit registration', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmployerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/employers`, employerForm);
      
      toast.success('Partnership request submitted!', {
        description: 'Our team will reach out within 24 hours.'
      });
      
      setEmployerForm({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        location: '',
        requirements: ''
      });
    } catch (error) {
      console.error('Error submitting employer form:', error);
      toast.error('Failed to submit request', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Careers</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Join the Skill Revolution
            </h1>
            <p className="text-xl text-blue-200 leading-relaxed">
              Whether you're a candidate seeking skill training or an employer looking for talented workforce, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-bold text-slate-900">For Candidates</h3>
              </div>
              <ul className="space-y-2">
                {['Free skill training under government schemes', 'Apprenticeship with leading companies', 'Job placement assistance', 'Government certified courses'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-slate-900">For Employers</h3>
              </div>
              <ul className="space-y-2">
                {['Access to skilled workforce', 'Government stipend support', 'Quick manpower mobilization', 'Dedicated support team'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-600 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Forms */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="candidate" className="text-base py-3">
                <UserCircle className="w-5 h-5 mr-2" />
                For Candidates
              </TabsTrigger>
              <TabsTrigger value="employer" className="text-base py-3">
                <Building2 className="w-5 h-5 mr-2" />
                For Employers
              </TabsTrigger>
            </TabsList>

            {/* Candidate Form */}
            <TabsContent value="candidate">
              <div className="bg-white rounded-2xl p-8 shadow-lg border">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Candidate Registration</h2>
                <p className="text-slate-600 mb-6">Register for skill training and job opportunities</p>
                
                <form onSubmit={handleCandidateSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={candidateForm.name}
                        onChange={handleCandidateChange}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="age">Age *</Label>
                      <Input
                        id="age"
                        name="age"
                        type="number"
                        min="18"
                        max="45"
                        value={candidateForm.age}
                        onChange={handleCandidateChange}
                        placeholder="Your age"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="qualification">Qualification *</Label>
                    <Select onValueChange={(value) => setCandidateForm(prev => ({ ...prev, qualification: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select qualification" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10th">10th Pass</SelectItem>
                        <SelectItem value="12th">12th Pass</SelectItem>
                        <SelectItem value="iti">ITI</SelectItem>
                        <SelectItem value="diploma">Diploma</SelectItem>
                        <SelectItem value="graduate">Graduate</SelectItem>
                        <SelectItem value="postgraduate">Post Graduate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location (Village/Town, District, State) *</Label>
                    <Input
                      id="location"
                      name="location"
                      value={candidateForm.location}
                      onChange={handleCandidateChange}
                      placeholder="Enter your location"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input
                        id="mobile"
                        name="mobile"
                        type="tel"
                        value={candidateForm.mobile}
                        onChange={handleCandidateChange}
                        placeholder="10-digit mobile"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={candidateForm.email}
                        onChange={handleCandidateChange}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="sector">Preferred Sector *</Label>
                    <Select onValueChange={(value) => setCandidateForm(prev => ({ ...prev, sector: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select preferred sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {careerSectors.map((sector) => (
                          <SelectItem key={sector} value={sector.toLowerCase()}>
                            {sector}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="resume">Upload Resume (Optional, Max 5MB)</Label>
                    <div className="mt-2">
                      {candidateForm.resume ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                          <Upload className="w-5 h-5 text-slate-500" />
                          <span className="text-sm text-slate-700 flex-1 truncate">
                            {candidateForm.resume.name}
                          </span>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            <X className="w-4 h-4 text-slate-500" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                        >
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload resume (PDF, DOC)</p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Register Now'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </div>
            </TabsContent>

            {/* Employer Form */}
            <TabsContent value="employer">
              <div className="bg-white rounded-2xl p-8 shadow-lg border">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Employer Registration</h2>
                <p className="text-slate-600 mb-6">Partner with us for skilled workforce mobilization</p>
                
                <form onSubmit={handleEmployerSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={employerForm.company_name}
                      onChange={handleEmployerChange}
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact_person">Contact Person *</Label>
                      <Input
                        id="contact_person"
                        name="contact_person"
                        value={employerForm.contact_person}
                        onChange={handleEmployerChange}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={employerForm.phone}
                        onChange={handleEmployerChange}
                        placeholder="Contact number"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="employerEmail">Email Address *</Label>
                    <Input
                      id="employerEmail"
                      name="email"
                      type="email"
                      value={employerForm.email}
                      onChange={handleEmployerChange}
                      placeholder="company@email.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="employerLocation">Location *</Label>
                    <Input
                      id="employerLocation"
                      name="location"
                      value={employerForm.location}
                      onChange={handleEmployerChange}
                      placeholder="City, State"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="requirements">Requirements *</Label>
                    <textarea
                      id="requirements"
                      name="requirements"
                      value={employerForm.requirements}
                      onChange={handleEmployerChange}
                      placeholder="Describe your workforce requirements, number of candidates needed, sectors, etc."
                      rows={4}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      required
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </Layout>
  );
};

export default CareersPage;
